import Select from 'react-select'
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { profileuploadimg } from "../../Routes/ImagePath"
import { customStyles, options } from "../../utils"
import { useForm, Controller } from "react-hook-form";
import {ClipLoader} from "react-spinners";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import alertify from "alertifyjs";
import { EmployeeSchema } from '../../utils/validationSchema'
import DatePicker from "react-datepicker";
import {
    useSaveEmployeesMutation,
    useUpdateEmployeeMutation,
    useLazyGetNextEmployeeCodeQuery,
    useLazyValidateZkTecoCodeQuery
} from '../../services/employee.service'
import { useGetCountriesQuery, useLazyGetStatesQuery } from '../../services/settings.service'
import { RELIGION_TYPES } from '../../constants/options.constants'


export const PersonalInfoForm = ({empId, profile, branches, departments, designations, onNext, user}) => {
    const [saveInfo, {isLoading, data, error}] = useSaveEmployeesMutation();
    const [updateInfo, {isLoading: isUpdating, data: updateData, error: updateError}] = useUpdateEmployeeMutation();
    const [getNextCode, {isLoading: isLoadingNextCode}] = useLazyGetNextEmployeeCodeQuery();
    const [validateCode, {isLoading: isValidatingCode}] = useLazyValidateZkTecoCodeQuery();
    const [codeValidationError, setCodeValidationError] = useState('');
    const [selectedCountryId, setSelectedCountryId] = useState(null);

    // Fetch countries and states
    const { data: countriesData } = useGetCountriesQuery(true);
    const [getStates, { data: statesData }] = useLazyGetStatesQuery();

    // Transform countries data to dropdown options
    const countryOptions = countriesData?.data?.map(country => ({
        value: country.id,
        label: country.name,
        name: country.name
    })) || [];

    // Transform states data to dropdown options
    const stateOptions = statesData?.data?.map(state => ({
        value: state.name,
        label: state.name
    })) || [];

    const {
        control,
        reset,
        getValues,
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(EmployeeSchema),
    });

    const pushToBiotime = watch('push_to_biotime');
    const zktecoEmpCode = watch('zkteco_emp_code');
    const nationality = watch('nationality');

    // Load states when country is selected
    useEffect(() => {
        if (selectedCountryId) {
            getStates(selectedCountryId);
        }
    }, [selectedCountryId, getStates]);

    // Load states for Nigeria by default or when profile nationality is Nigeria
    useEffect(() => {
        const nigeriaCountry = countriesData?.data?.find(c => c.code === 'NG');
        if (nigeriaCountry && (!nationality || nationality === nigeriaCountry.id)) {
            setSelectedCountryId(nigeriaCountry.id);
            getStates(nigeriaCountry.id);
        }
    }, [countriesData, nationality]);

    useEffect(() => {
        if (data && data.success) {
            onNext(data?.data.id);
        }

        if (updateData && updateData?.success) {
            onNext(updateData?.data);
            alertify.success('Employee information updated successfully');
        }

        if ((data && !data.success) || (updateData && !updateData.success)) {
            alertify.error(data?.message || updateData?.message);
        }

        if (error || updateError) {
            const errorObj = error || updateError;

            // Handle validation errors from backend
            if (errorObj?.data?.message) {
                if (Array.isArray(errorObj.data.message)) {
                    // Display each validation error
                    errorObj.data.message.forEach((msg) => {
                        alertify.error(msg);
                    });
                } else {
                    alertify.error(errorObj.data.message);
                }
            } else if (errorObj?.message) {
                alertify.error(errorObj.message);
            } else {
                alertify.error('Failed to save employee information. Please check all required fields.');
            }
        }
    }, [data, updateData, error, updateError]);

    const save = payload => {
        // Validate ZkTeco employee code if push to biotime is enabled
        if (payload.push_to_biotime && codeValidationError) {
            alertify.error('Please fix the ZkTeco employee code error before submitting');
            return;
        }

        if (empId) {
            updateInfo({data: payload, id: empId});
        } else {
            saveInfo(payload);
        }
    }
    
    useEffect(() => {
        if (profile) {
            const form = {...profile};
            form.designation_id =  profile?.designation?.id;
            form.department_id =  profile?.department?.id;
            form.branch_id =  profile?.branch?.id;

            reset(form);
        }
    }, [profile]);

    // Fetch next available employee code when push_to_biotime is checked
    useEffect(() => {
        if (pushToBiotime && !zktecoEmpCode) {
            getNextCode()
                .unwrap()
                .then((response) => {
                    if (response?.success && response?.data?.next_employee_code) {
                        setValue('zkteco_emp_code', response.data.next_employee_code);
                        setCodeValidationError('');
                    }
                })
                .catch((err) => {
                    console.error('Failed to fetch next employee code:', err);
                    alertify.error('Failed to fetch next employee code');
                });
        }
    }, [pushToBiotime]);

    // Validate employee code when it changes
    useEffect(() => {
        if (pushToBiotime && zktecoEmpCode && zktecoEmpCode.trim()) {
            const timeoutId = setTimeout(() => {
                validateCode(zktecoEmpCode)
                    .unwrap()
                    .then((response) => {
                        if (response?.success && response?.data?.exists) {
                            setCodeValidationError('This employee code already exists in ZkTeco Biotime');
                        } else {
                            setCodeValidationError('');
                        }
                    })
                    .catch((err) => {
                        console.error('Failed to validate employee code:', err);
                    });
            }, 500); // Debounce validation

            return () => clearTimeout(timeoutId);
        } else {
            setCodeValidationError('');
        }
    }, [zktecoEmpCode, pushToBiotime]);
      
    return (
            <form onSubmit={handleSubmit(save)}>
                <div className="form-upload-profile">
                    <h6 className="">
                        Profile Image <span> *</span>
                    </h6>
                    <div className="profile-pic-upload">
                        <div className="profile-pic">
                            <span>
                                <img
                                    src={profileuploadimg}
                                    alt="Img"
                                />
                            </span>
                        </div>
                        <div className="employee-field">
                            <div className="mb-0">
                                <div className="image-upload mb-0">
                                    <input type="file" />
                                    <div className="image-uploads">
                                        <h4>Upload</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="img-reset-btn">
                                <Link to="#">Reset</Link>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="contact-input-set">
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    First Name <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="first_name"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                    <input
                                        className={`form-control ${
                                        errors?.first_name ? "is-invalid" : ""
                                        }`}
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        autoComplete="false"
                                    />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.first_name?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Last Name <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="last_name"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                    <input
                                        className={`form-control ${
                                        errors?.last_name ? "is-invalid" : ""
                                        }`}
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        autoComplete="false"
                                    />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.last_name?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Job Title <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="designation_id"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select"
                                            styles={customStyles}
                                            value={designations?.find(c => c.value === value)}
                                            optionFilterProp="children"
                                            onChange={({value}) => onChange(value)}
                                            options={designations || []}
                                        />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.designation_id?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-8">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Address <span className="text-danger">*</span>
                                </label>
                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                    <input
                                        className={`form-control ${
                                        errors?.address ? "is-invalid" : ""
                                        }`}
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        autoComplete="false"
                                    />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.address?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Place of Birth 
                                </label>
                                <Controller
                                    name="place_of_birth"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <input
                                            className={`form-control`}
                                            type="text"
                                            value={value}
                                            onChange={onChange}
                                            autoComplete="false"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Phone Number 1<span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <input
                                            className={`form-control ${
                                            errors?.phone ? "is-invalid" : ""
                                            }`}
                                            type="text"
                                            value={value}
                                            onChange={onChange}
                                            autoComplete="false"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Phone Number 2
                                </label>
                                <Controller
                                    name="phone_number_2"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <input
                                            className={`form-control`}
                                            type="text"
                                            value={value}
                                            onChange={onChange}
                                            autoComplete="false"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="col-form-label">
                                        Email <span className="text-danger"> *</span>
                                    </label>
                                </div>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <input
                                            className={`form-control ${
                                            errors?.email ? "is-invalid" : ""
                                            }`}
                                            type="email"
                                            value={value}
                                            onChange={onChange}
                                            autoComplete="false"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Gender <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="gender"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select"
                                            styles={customStyles}
                                            optionFilterProp="children"
                                            value={options.genders.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            options={options.genders}
                                        />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.gender?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Marital Status <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="marital_status"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select"
                                            styles={customStyles}
                                            value={options.maritalStatus.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            optionFilterProp="children"
                                            options={options.maritalStatus}
                                        />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.marital_status?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="col-form-label">
                                        Date of Birth <span className="text-danger"> *</span>
                                    </label>
                                </div>
                                <Controller
                                    name="date_of_birth"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="cal-icon">
                                            <DatePicker
                                                selected={value || null}
                                                onChange={onChange}
                                                className="form-control floating datetimepicker"
                                                type="date"
                                                dateFormat="dd-MM-yyyy"
                                            />
                                        </div>
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.date_of_birth?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="col-form-label">
                                        Nationality
                                    </label>
                                </div>
                                <Controller
                                    name="nationality"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select Country"
                                            styles={customStyles}
                                            value={countryOptions.find(c => c.name === value)}
                                            onChange={(selected) => {
                                                onChange(selected.name);
                                                setSelectedCountryId(selected.value);
                                                // Clear state of origin when country changes
                                                setValue('state_of_origin', '');
                                            }}
                                            optionFilterProp="children"
                                            options={countryOptions}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="col-form-label">
                                        State of Origin
                                    </label>
                                </div>
                                <Controller
                                    name="state_of_origin"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select State"
                                            styles={customStyles}
                                            value={stateOptions.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            optionFilterProp="children"
                                            options={stateOptions}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="col-form-label">
                                        Religion
                                    </label>
                                </div>
                                <Controller
                                    name="religion"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select Religion"
                                            styles={customStyles}
                                            value={RELIGION_TYPES.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            optionFilterProp="children"
                                            options={RELIGION_TYPES}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Branch <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="branch_id"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            isDisabled={
                                                !['HR Admin', 'Admin', 'HR Assistant'].includes(
                                                    user?.role
                                                )
                                            }
                                            className="select"
                                            placeholder="Select"
                                            value={branches?.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            styles={customStyles}
                                            optionFilterProp="children"
                                            options={branches || []}
                                        />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.branch_id?.message}
                                </span>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Department <span className="text-danger"> *</span>
                                </label>
                                <Controller
                                    name="department_id"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            className="select"
                                            placeholder="Select"
                                            value={departments?.find(c => c.value === value)}
                                            onChange={({value}) => onChange(value)}
                                            styles={customStyles}
                                            optionFilterProp="children"
                                            options={departments || []}
                                        />
                                    )}
                                />
                                <span className="text-danger">
                                    {errors?.department_id?.message}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <label className="col-form-label">
                                    Date Joined
                                </label>
                                <Controller
                                    name="date_of_joining"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="cal-icon">
                                            <DatePicker
                                                selected={value || null}
                                                onChange={onChange}
                                                className="form-control floating datetimepicker"
                                                type="date"
                                                dateFormat="dd-MM-yyyy"
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="col-lg-12">
                            <div className="input-block mb-3">
                                <h5 className="mb-3">ZkTeco Biotime Integration</h5>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="input-block mb-3">
                                <div className="d-flex align-items-center">
                                    <Controller
                                        name="push_to_biotime"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <input
                                                className="form-check-input me-2"
                                                type="checkbox"
                                                checked={value || false}
                                                onChange={(e) => onChange(e.target.checked)}
                                                id="push_to_biotime"
                                            />
                                        )}
                                    />
                                    <label className="form-check-label" htmlFor="push_to_biotime">
                                        Push employee to ZkTeco Biotime
                                    </label>
                                </div>
                                <small className="text-muted">
                                    Enable this to automatically sync employee to the biometric attendance system
                                </small>
                            </div>
                        </div>

                        {pushToBiotime && (
                            <div className="col-lg-4 col-md-6">
                                <div className="input-block mb-3">
                                    <label className="col-form-label">
                                        ZkTeco Employee Code <span className="text-danger"> *</span>
                                        {isLoadingNextCode && <ClipLoader size={12} className="ms-2" />}
                                    </label>
                                    <Controller
                                        name="zkteco_emp_code"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <div className="position-relative">
                                                <input
                                                    className={`form-control ${
                                                        errors?.zkteco_emp_code || codeValidationError ? "is-invalid" : ""
                                                    }`}
                                                    type="text"
                                                    value={value || ''}
                                                    onChange={onChange}
                                                    placeholder="Enter employee code for biometric system"
                                                    autoComplete="false"
                                                    disabled={isLoadingNextCode}
                                                />
                                                {isValidatingCode && (
                                                    <div className="position-absolute" style={{right: '10px', top: '50%', transform: 'translateY(-50%)'}}>
                                                        <ClipLoader size={16} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                    {codeValidationError && (
                                        <span className="text-danger d-block">
                                            {codeValidationError}
                                        </span>
                                    )}
                                    {errors?.zkteco_emp_code?.message && (
                                        <span className="text-danger d-block">
                                            {errors?.zkteco_emp_code?.message}
                                        </span>
                                    )}
                                    {!codeValidationError && zktecoEmpCode && !isValidatingCode && (
                                        <small className="text-success d-block">
                                            ✓ Employee code is available
                                        </small>
                                    )}
                                    <small className="text-muted">
                                        Unique identifier for the employee in ZkTeco Biotime
                                    </small>
                                </div>
                            </div>
                        )}

                        <div className="col-lg-12 d-flex justify-content-between form-wizard-button">
                            <button
                                className="button btn-lights reset-btn"
                                type="reset"
                                onClick={reset}
                            >
                                Reset
                            </button>
                            <div className='text-end'>
                                
                                <button
                                    className="btn btn-primary wizard-next-btn"
                                    type="submit" 
                                >
                                    {(!isLoading || isUpdating) ? 'Save & Next' : <ClipLoader color='#ffffff' />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
    )
}